import { useEffect, useMemo, useState } from 'react';
import { API_GET, DBRequest, useConfig } from '../../../api/api';
import TypeSkeleton from './TypeSkeleton';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Collapse,
  IconButton,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

const TypeTaxAssignment = ({
  updateValidationOnChange,
  dataCollection,
  field,
  errors,
  onPreventEnter,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  // data
  const [taxGroups, setTaxGroups] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const config = useConfig();

  useEffect(() => {
    if (!config) return;
    DBRequest({
      config,
      path: 'taxassignmentgroups',
      method: API_GET,
      onResponse: setTaxGroups,
      onLoading: setIsLoading,
    });
  }, [config]);

  // Flatten all tax accounts with group info
  const flattenedAccounts = useMemo(() => {
    if (!taxGroups?.data) return [];
    return taxGroups.data.flatMap((group) =>
      group.taxAssignments.map((account) => ({
        ...account,
        groupName: group.groupName,
        groupColor: colors.colorPool[group.colorPoolId],
        groupId: group.id,
      })),
    );
  }, [taxGroups, colors.colorPool]);

  // Info panel expansion state
  const [expanded, setExpanded] = useState(false);
  // Show alternatives from same group
  const [showAlternatives, setShowAlternatives] = useState(false);

  // Current selected value
  const selectedValue = dataCollection[field.key] || null;

  // Find the full account object with group info for the selected value
  const selectedAccount = useMemo(() => {
    if (!selectedValue || !flattenedAccounts.length) return null;
    return (
      flattenedAccounts.find((acc) => acc.id === selectedValue.id) ||
      selectedValue
    );
  }, [selectedValue, flattenedAccounts]);

  // Get alternative accounts from the same group
  const alternativeAccounts = useMemo(() => {
    if (!selectedAccount || !flattenedAccounts.length) return [];
    return flattenedAccounts.filter(
      (acc) =>
        acc.groupName === selectedAccount.groupName &&
        acc.id !== selectedAccount.id,
    );
  }, [selectedAccount, flattenedAccounts]);

  // Handle selection change
  function handleChange(_, newValue) {
    updateValidationOnChange([field.key], newValue);
    setExpanded(false);
  }

  // Clear selection
  function handleClear() {
    updateValidationOnChange([field.key], null);
    setExpanded(false);
    setShowAlternatives(false);
  }

  // Select alternative account
  function handleSelectAlternative(account) {
    updateValidationOnChange([field.key], account);
    setShowAlternatives(false);
    setExpanded(false);
  }

  // Toggle alternatives view
  function handleToggleAlternatives(e) {
    e.stopPropagation();
    setShowAlternatives(!showAlternatives);
    setExpanded(false);
  }

  // Custom filter function - search across multiple fields
  const filterOptions = (options, { inputValue }) => {
    const search = inputValue.toLowerCase().trim();
    if (!search) return options;

    return options.filter(
      (opt) =>
        opt.account?.toLowerCase().includes(search) ||
        opt.label?.toLowerCase().includes(search) ||
        opt.groupName?.toLowerCase().includes(search) ||
        opt.description?.toLowerCase().includes(search),
    );
  };

  // Prevent form submission on Enter when autocomplete is open
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  useEffect(() => {
    onPreventEnter(autocompleteOpen);
  }, [autocompleteOpen, onPreventEnter]);

  return (
    <Box className="col-span-2 flex flex-col gap-2">
      {isLoading ? (
        <TypeSkeleton />
      ) : selectedAccount ? (
        // Selected Account Display
        <Box
          sx={{
            borderRadius: 1,
          }}
        >
          {/* Header with account info and close button */}
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 40,
                backgroundColor: selectedAccount.groupColor,
                borderRadius: 1,
                flexShrink: 0,
              }}
            />
            <Box
              sx={{ flex: 1, minWidth: 0 }}
              onClick={() => setExpanded(!expanded)}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary">
                  {field.label}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="body1"
                  fontFamily="monospace"
                  fontWeight="bold"
                >
                  {selectedAccount.account}
                </Typography>
                <Typography variant="body1" noWrap>
                  {selectedAccount.label}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`${selectedAccount.groupName}${alternativeAccounts.length > 0 ? ` (${alternativeAccounts.length})` : ''}`}
              size="small"
              onClick={
                alternativeAccounts.length > 0
                  ? handleToggleAlternatives
                  : undefined
              }
              sx={{
                backgroundColor: selectedAccount.groupColor,
                color: theme.palette.getContrastText(
                  selectedAccount.groupColor || '#000',
                ),
                fontWeight: 500,
                cursor: alternativeAccounts.length > 0 ? 'pointer' : 'default',
                '&:hover':
                  alternativeAccounts.length > 0
                    ? {
                        opacity: 0.85,
                      }
                    : {},
              }}
            />
            <ExpandMoreIcon
              onClick={() => setExpanded(!expanded)}
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                color: colors.grey[500],
                cursor: 'pointer',
              }}
            />
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{
                color: colors.grey[500],
                '&:hover': {
                  color: colors.grey[300],
                  backgroundColor: colors.grey[800],
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Expandable description */}
          <Collapse in={expanded}>
            <Box
              sx={{
                px: 2,
                pb: 1.5,
                pt: 0,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, lineHeight: 1.6 }}
              >
                {selectedAccount.description || t('No description available')}
              </Typography>
            </Box>
          </Collapse>

          {/* Alternative accounts from same group */}
          <Collapse in={showAlternatives}>
            <Box
              sx={{
                px: 1.5,
                pb: 1.5,
                pt: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'block' }}
              >
                {t('Alternative accounts in')} "{selectedAccount.groupName}":
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {alternativeAccounts.map((account) => (
                  <Box
                    key={account.id}
                    onClick={() => handleSelectAlternative(account)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      backgroundColor: colors.bgInput,
                      '&:hover': {
                        backgroundColor: colors.selected,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 24,
                        backgroundColor: account.groupColor,
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontWeight="bold"
                      sx={{ minWidth: 50 }}
                    >
                      {account.account}
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {account.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Collapse>
        </Box>
      ) : (
        // Search Autocomplete
        <Autocomplete
          options={flattenedAccounts}
          value={null}
          onChange={handleChange}
          onOpen={() => setAutocompleteOpen(true)}
          onClose={() => setAutocompleteOpen(false)}
          filterOptions={filterOptions}
          groupBy={(option) => option.groupName}
          getOptionLabel={(option) =>
            option ? `${option.account} - ${option.label}` : ''
          }
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              placeholder={t(
                'Search by account number, name or description...',
              )}
            />
          )}
          renderGroup={(params) => (
            <li key={params.key}>
              <Box
                sx={{
                  position: 'sticky',
                  top: '-8px',
                  px: 2,
                  py: 1,
                  backgroundColor: colors.bgInput,
                  borderBottom: `1px solid ${colors.grey[800]}`,
                  zIndex: 1,
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {params.group}
                </Typography>
              </Box>
              <ul style={{ padding: 0 }}>{params.children}</ul>
            </li>
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <Box
                component="li"
                key={key}
                {...otherProps}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                  '&:hover': {
                    backgroundColor: colors.selected,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 32,
                    backgroundColor: option.groupColor,
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 60 }}>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    fontWeight="bold"
                  >
                    {option.account}
                  </Typography>
                </Box>
                <Typography variant="body2" noWrap>
                  {option.label}
                </Typography>
              </Box>
            );
          }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: colors.glass,
                maxHeight: 400,
                backdropFilter: 'blur(8px)',
              },
            },
          }}
          noOptionsText={t('No tax accounts found')}
          clearText={t('Clear')}
          openText={t('Open')}
          closeText={t('Close')}
        />
      )}
    </Box>
  );
};

export default TypeTaxAssignment;
